// One training: its landing page at a glance and every run of it, newest
// first, filterable by edition and status. Editions are added and edited here;
// each opens its own page for its schedule, marketing report and feedback form.

import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate,
	useRouteContext,
} from "@tanstack/react-router";
import {
	ArrowRight,
	ChevronLeft,
	ExternalLink,
	Loader2,
	Pencil,
	Plus,
	Trash2,
} from "lucide-react";
import { useState } from "react";
import {
	EditionDialog,
	EditionFilters,
	type EditionFilterValue,
	EditionFollowUp,
	type EditionRow,
	EditionStatusBadge,
	matchesEditionFilter,
	ROW_ACTION,
} from "#/components/admin/editions";
import { GhostButton, PrimaryButton } from "#/components/admin/modal";
import {
	TrainingDialogLoader,
	useTrainingRemoval,
} from "#/components/admin/training-dialog";
import {
	asTemplateKey,
	TEMPLATE_LABELS,
} from "#/components/templates/registry";
import { authClient } from "#/lib/auth-client";
import { editionLabel, isoDay, pickCurrentEdition } from "#/lib/editions";
import { algiersToday } from "#/lib/lead-filters";
import {
	canManageEditions,
	canManageTrainings,
	canViewTrainings,
} from "#/lib/roles";
import { getSession } from "#/lib/session";

export const Route = createFileRoute("/admin/trainings_/$trainingId")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/admin/login" });
		}
		// The community manager has the schedule, not the trainings themselves.
		if (!canViewTrainings(session.user.role)) {
			throw redirect({ to: "/admin/trainings" });
		}
	},
	component: TrainingPage,
});

// A training has few runs, so its page opens on all of them.
const ALL_RUNS: EditionFilterValue = {
	training: "all",
	edition: "all",
	status: "all",
};

function TrainingPage() {
	const { trainingId } = Route.useParams();
	const { trpc } = useRouteContext({ from: "__root__" });
	const { data: session } = authClient.useSession();
	const role = session?.user?.role;
	const [editingTraining, setEditingTraining] = useState(false);
	const [editionDialog, setEditionDialog] = useState<EditionRow | "new" | null>(
		null,
	);
	const [filter, setFilter] = useState(ALL_RUNS);
	const navigate = useNavigate();
	const { remove, isPending: removing } = useTrainingRemoval(() => {
		void navigate({ to: "/admin/trainings" });
	});

	const trainingsQuery = useQuery(trpc.trainings.list.queryOptions());
	const editionsQuery = useQuery(trpc.editions.list.queryOptions());
	const today = algiersToday();

	if (trainingsQuery.isLoading || editionsQuery.isLoading) {
		return (
			<div className="flex justify-center py-20">
				<Loader2 className="h-6 w-6 animate-spin text-muted" />
			</div>
		);
	}

	const training = trainingsQuery.data?.find((t) => t.id === trainingId);
	if (!training) {
		return (
			<div className="flex flex-col items-center gap-3 py-20 text-center">
				<p className="text-sm font-semibold text-red-600">التدريب غير موجود.</p>
				<Link
					to="/admin/trainings"
					className="text-sm font-bold text-teal-600 hover:underline"
				>
					العودة إلى التدريبات
				</Link>
			</div>
		);
	}

	const allRuns = ((editionsQuery.data ?? []) as EditionRow[])
		.filter((edition) => edition.trainingId === trainingId)
		.sort((a, b) => b.editionNumber - a.editionNumber);
	const runs = allRuns.filter((run) =>
		matchesEditionFilter(run, filter, today),
	);
	const current = pickCurrentEdition(allRuns, today);
	const confirmed = allRuns.reduce((sum, run) => sum + run._count.leads, 0);
	const reports = allRuns.filter((run) => run.marketingReport).length;
	const responses = allRuns.reduce(
		(sum, run) => sum + (run.questionnaire?._count.responses ?? 0),
		0,
	);

	return (
		<div className="flex flex-col gap-5">
			<header className="flex flex-col gap-3">
				<Link
					to="/admin/trainings"
					className="inline-flex w-fit items-center gap-1 text-xs font-bold text-muted hover:text-navy"
				>
					<ArrowRight className="h-3.5 w-3.5" />
					التدريبات
				</Link>
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div className="min-w-0">
						<h1 className="text-2xl font-bold text-navy">{training.name}</h1>
						<div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
							<span
								className={`rounded-md px-2 py-1 font-semibold ${
									training.isActive
										? "bg-teal-50 text-teal-600"
										: "bg-line/50 text-muted"
								}`}
							>
								{training.isActive ? "مفعّل" : "معطّل"}
							</span>
							{training.isDefault ? (
								<span className="rounded-md bg-gold-50 px-2 py-1 font-semibold text-gold-600">
									افتراضي
								</span>
							) : null}
							<span className="text-muted">
								{TEMPLATE_LABELS[asTemplateKey(training.template)]}
							</span>
							{training.isActive ? (
								<a
									href={`/training/${training.slug}`}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 font-semibold text-teal-600 hover:underline"
									dir="ltr"
								>
									/training/{training.slug}
									<ExternalLink className="h-3 w-3" />
								</a>
							) : (
								<span className="text-muted" dir="ltr">
									/training/{training.slug}
								</span>
							)}
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{canManageTrainings(role) ? (
							<GhostButton onClick={() => setEditingTraining(true)}>
								<Pencil className="h-4 w-4" />
								تعديل صفحة الهبوط
							</GhostButton>
						) : null}
						{canManageTrainings(role) ? (
							<button
								type="button"
								onClick={() => remove(training, allRuns.length, confirmed)}
								disabled={removing}
								className="inline-flex items-center justify-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
							>
								<Trash2 className="h-4 w-4" />
								حذف
							</button>
						) : null}
						{canManageEditions(role) ? (
							<PrimaryButton onClick={() => setEditionDialog("new")}>
								<Plus className="h-4 w-4" />
								طبعة جديدة
							</PrimaryButton>
						) : null}
					</div>
				</div>
			</header>

			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
				<StatCard label="الطبعات" value={String(allRuns.length)} />
				<StatCard
					label="الطبعة الحالية"
					value={current ? editionLabel(current.editionNumber) : "—"}
					extra={
						current ? (
							<EditionStatusBadge edition={current} today={today} />
						) : null
					}
				/>
				<StatCard
					label="الطلبات المؤكدة"
					value={String(confirmed)}
					extra={
						confirmed ? (
							<Link
								to="/admin/leads"
								search={{ trainingId: training.id }}
								className="text-xs font-bold text-teal-600 hover:underline"
							>
								عرض الطلبات
							</Link>
						) : null
					}
				/>
				<StatCard
					label="المتابعة"
					value={`${reports} / ${allRuns.length} تقرير`}
					extra={
						<span className="text-xs text-muted">{responses} إجابة تقييم</span>
					}
				/>
			</div>

			<section className="flex flex-col gap-3">
				<div className="flex flex-wrap items-end justify-between gap-3">
					<h2 className="font-display text-base font-bold text-navy">
						الطبعات{" "}
						<span className="text-sm font-semibold text-muted">
							({runs.length}
							{runs.length !== allRuns.length ? ` من ${allRuns.length}` : ""})
						</span>
					</h2>
					{allRuns.length ? (
						<EditionFilters
							editions={allRuns}
							value={filter}
							defaultValue={ALL_RUNS}
							onChange={setFilter}
						/>
					) : null}
				</div>

				{runs.length ? (
					runs.map((run) => (
						<EditionCard
							key={run.courseNumber}
							edition={run}
							today={today}
							canManage={canManageEditions(role)}
							onEdit={() => setEditionDialog(run)}
						/>
					))
				) : allRuns.length ? (
					<p className="rounded-2xl border border-dashed border-line bg-white px-6 py-10 text-center text-sm text-muted">
						لا توجد طبعات لهذا الفلتر.
					</p>
				) : (
					<div className="rounded-2xl border border-dashed border-line bg-white px-6 py-12 text-center">
						<p className="font-bold text-navy">
							لا توجد طبعات لهذا التدريب بعد.
						</p>
						{canManageEditions(role) ? (
							<button
								type="button"
								onClick={() => setEditionDialog("new")}
								className="mt-2 text-sm font-bold text-teal-600 hover:underline"
							>
								إضافة الطبعة الأولى
							</button>
						) : null}
					</div>
				)}
			</section>

			{editingTraining ? (
				<TrainingDialogLoader
					trainingId={training.id}
					onClose={() => setEditingTraining(false)}
				/>
			) : null}

			{editionDialog ? (
				<EditionDialog
					edition={editionDialog === "new" ? null : editionDialog}
					defaultTrainingId={training.id}
					onClose={() => setEditionDialog(null)}
				/>
			) : null}
		</div>
	);
}

function StatCard({
	label,
	value,
	extra,
}: {
	label: string;
	value: string;
	extra?: React.ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1 rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
			<span className="text-xs font-bold text-muted">{label}</span>
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-lg font-bold text-navy">{value}</span>
				{extra}
			</div>
		</div>
	);
}

function EditionCard({
	edition,
	today,
	canManage,
	onEdit,
}: {
	edition: EditionRow;
	today: string;
	canManage: boolean;
	onEdit: () => void;
}) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white px-5 py-4 shadow-card">
			<div className="flex min-w-0 flex-col gap-1.5">
				<div className="flex flex-wrap items-center gap-2">
					<Link
						to="/admin/editions/$editionId"
						params={{ editionId: String(edition.courseNumber) }}
						className="font-bold text-navy hover:text-teal-600 hover:underline"
					>
						{editionLabel(edition.editionNumber)}
					</Link>
					<span className="text-xs text-muted">#{edition.courseNumber}</span>
					<EditionStatusBadge edition={edition} today={today} />
				</div>
				<p className="text-sm text-ink">
					<bdi dir="ltr">
						{isoDay(edition.startDate)} → {isoDay(edition.endDate)}
					</bdi>{" "}
					· {edition.trainerName}
				</p>
				<div className="flex flex-wrap items-center gap-1">
					<Link
						to="/admin/leads"
						search={{
							trainingId: edition.trainingId,
							editions: [String(edition.courseNumber)],
						}}
						className="whitespace-nowrap rounded-md bg-navy/5 px-2 py-0.5 text-xs font-semibold text-navy transition-colors hover:bg-navy/10"
					>
						{edition._count.leads} طلب مؤكد
					</Link>
					<EditionFollowUp edition={edition} />
				</div>
			</div>
			<div className="flex flex-wrap items-center gap-1.5">
				{canManage ? (
					<button type="button" onClick={onEdit} className={ROW_ACTION}>
						<Pencil className="h-3.5 w-3.5" />
						تعديل
					</button>
				) : null}
				<Link
					to="/admin/editions/$editionId"
					params={{ editionId: String(edition.courseNumber) }}
					className={ROW_ACTION}
				>
					فتح الطبعة
					<ChevronLeft className="h-3.5 w-3.5" />
				</Link>
			</div>
		</div>
	);
}
